import { Component, Input, OnDestroy, OnInit, ViewChild, WritableSignal, isDevMode, signal, ChangeDetectionStrategy } from '@angular/core';
import { AuthService } from '../../../../services/auth.service';
import { BackOfficeService } from '../../../../services/backoffice.service';
import { takeUntil } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import { MyConfigOrganization } from '../../../../models/myConfig';
import { EditOrganizationDiscountCheckRequest, Order, OrganizationDiscountCheckVM, OrganizationParkingPricingVM, OrganizationParkingsVM } from '../../../..//models/orderModel';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { DiscountFormComponent } from './discount-form/discount-form.component';
import { AdditionalServicesFormComponent } from './additional-services-form/additional-services-form.component';
import { ParkingVM } from '../../../../models/parkingsite.model';
import { ParkingService } from '../../../../services/parking.service';
import { ParkingPricingFormComponent } from './parking-pricing-form/parking-pricing-form.component';
import { MyConfigService } from '../../../../services/my-config.service';
import { ParkingPricingBulkUpdateFormComponent } from './parking-pricing-bulk-update-form/parking-pricing-bulk-update-form.component';
import { BackOffice24StatesService } from '../state/back-office24-states.service';

@Component({
  selector: 'app-back-office24-pricing',
  templateUrl: './back-office24-pricing.component.html',
  styleUrl: './back-office24-pricing.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class BackOffice24PricingComponent implements OnInit, OnDestroy {
  componentDestroyed$: Subject<boolean> = new Subject();
  @Input() organization: MyConfigOrganization;
  private orgSubscription: Subscription = new Subscription();
  organizationDiscountChecks: OrganizationDiscountCheckVM[] = [];
  dataSourceDiscountCodes = new MatTableDataSource<any>();
  order: Order;
  selectedFilter: string = 'active';
  parkingVMs: ParkingVM[] = [];
  activeRequests: number = 0;
  isLoading: boolean = false;
  parkingPricingId: string = "";
  emptyData = new MatTableDataSource([{ empty: "row" }]);

  displayedColumnsCheckIn: string[] = [
    // 'bundleName',
    'discountCode',
    'discountPercent',
    'discountAmount',
    'validFrom',
    'validTo',
    'parkingName',
    'created',
    'status',
    'discountTypeName'
  ];
  productTypeIds: number[] = [];
  productTypes: any[] = [
    { name: { en: "Parking", sv: "Parkering" }, value: 1 },
    { name: { en: "DriveIn", sv: "DriveIn" }, value: 11 },
    { name: { en: "Subscription", sv: "Abonnemang" }, value: 10 },
    { name: { en: "Ext1", sv: "Ext1" }, value: 12 },
    { name: { en: "Ext2", sv: "Ext2" }, value: 13 },
    { name: { en: "Ext3", sv: "Ext3" }, value: 14 },
    { name: { en: "Ext0", sv: "Ext0" }, value: 15 }
  ];

  @ViewChild('sortCheckIn', { static: false }) sortCheckIn!: MatSort;

  organizationAdditionalServices = signal<any[]>([]);
  organizationParkingPrices = signal<any[]>([]);
  parkingId: string;
  allParkingPrices: OrganizationParkingPricingVM[];

  constructor(
    public authService: AuthService,
    private BackOfficeService: BackOfficeService,
    public router: Router,
    public dialog: MatDialog,
    public myConfigService: MyConfigService,
    public backOffice24StatesService: BackOffice24StatesService,
  ) { }

  ngOnInit(): void {
    this.orgSubscription = this.backOffice24StatesService.currentOrganization$.subscribe(
      org => this.organization = org
    );
    try {
      this.parkingPricingId = this.organization.config.parkings[0].parkingId;
      this.initConfig();
    } catch (e) {
      console.log(e);
    }
  }

  initConfig() {
    this.getOrganizationDiscountChecks();
    this.getOrganizationAdditionalServices();
    this.getOrganizationParkingPrices();
  }

  private setLoadingState(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  private incrementRequests() {
    this.activeRequests++;
    this.setLoadingState(true);
  }

  private decrementRequests() {
    if (this.activeRequests > 0)
      this.activeRequests--;
    if (this.activeRequests === 0) {
      this.setLoadingState(false);
    }
  }


  ngAfterViewInit() {
    this.dataSourceDiscountCodes.sort = this.sortCheckIn;
    this.dataSourceDiscountCodes.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'parkingName': return item.parkingName || '';
        case 'discountCode': return item.discountCode || '';
        case 'bundleName': return item.bundleName || '';
        case 'discountPercent': return item.discountPercent || 0;
        case 'discountAmount': return item.discountAmount || 0;
        case 'validFrom': return new Date(item.validFrom).getTime();
        case 'validTo': return new Date(item.validTo).getTime();
        case 'created': return new Date(item.created).getTime();
        case 'status': return item.status || '';
        case 'discountTypeName': return item.discountTypeName || [''];
        default: return item[property];
      }
    };
  }

  getOrganizationDiscountChecks() {
    this.incrementRequests();
    this.BackOfficeService
      .getOrganizationDiscountChecks(this.organization.organizationId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(discountChecks => {
        this.organizationDiscountChecks = discountChecks;
        this.populateDiscountCodeStatus();
        // this.organizationDiscountChecks.forEach((item: any) => {
        //   item.discountTypeName = item.discountTypeName.split(',');
        // });
        this.dataSourceDiscountCodes.data = this.organizationDiscountChecks;
        this.applyFilter();
        setTimeout(() => {
          this.dataSourceDiscountCodes.sort = this.sortCheckIn;
        });

        this.decrementRequests();
      },
        () => {
          this.decrementRequests();
        });
  }

  populateDiscountCodeStatus() {
    const currentTime = new Date();
    const swedenTime = new Date(currentTime.toLocaleString("en-US", { timeZone: "Europe/Stockholm" }));

    this.organizationDiscountChecks.forEach(discountCheck => {
      if (discountCheck.validTo) {
        const validTo = new Date(discountCheck.validTo);
        if (validTo <= swedenTime) {
          discountCheck.status = "Expired";
        } else if (validTo >= swedenTime) {
          discountCheck.status = "Active";
        }
      } else {
        discountCheck.status = "Active";
      }
    });
  }

  openEditPricingModal(pricing?: any) {
    if (!this.isLoading) {
      var model: any = pricing ? pricing : null;

      const dialogRef = this.dialog.open(DiscountFormComponent, {
        // height: '1010px',
        // minHeight: '80vh',
        width: '720px',
        hasBackdrop: true,
        data: {
          pricing: model,
          organization: this.organization,
          edit: pricing ? true : false,
          // parkings: this.parkingVMs
        }
      });

      dialogRef.afterOpened().subscribe(() => {
        const componentInstance = dialogRef.componentInstance as DiscountFormComponent;
        componentInstance.saveChangesEvent.subscribe((discountCheck) => {
          this.updateDiscounts(discountCheck);
          this.getOrganizationDiscountChecks();
        });
      });
    }
  }

  updateDiscounts(discountCheck) {
    const index = this.dataSourceDiscountCodes.data.findIndex(dc => dc.id === discountCheck.id);
    if (index > -1) {
      this.organizationDiscountChecks[index] = discountCheck;
    }
    else {
      this.organizationDiscountChecks.unshift(discountCheck);
    }
    this.populateDiscountCodeStatus();
    // this.organizationDiscountChecks.forEach((item: any) => {
    //   item.discountTypeName = item.discountTypeName.split(',');
    // });
    this.dataSourceDiscountCodes.data = this.organizationDiscountChecks
  }

  openEditAdditionalServiceModal(additionalService?: any) {
    if (!this.isLoading) {
      var model: any = additionalService ? additionalService : null;

      const dialogRef = this.dialog.open(AdditionalServicesFormComponent, {
        // height: '1010px',
        // minHeight: '80vh',
        width: '720px',
        hasBackdrop: true,
        data: {
          additionalService: model,
          organization: this.organization,
          edit: additionalService ? true : false,

        }
      });

      dialogRef.afterOpened().subscribe(() => {
        const componentInstance = dialogRef.componentInstance as AdditionalServicesFormComponent;
        componentInstance.saveChangesEvent.subscribe((additionalService) => {
          this.updateAdditionalServices(additionalService);
        });
      });
    }
  }

  updateAdditionalServices(additionalService) {
    const currentServices = this.organizationAdditionalServices();
    const index = currentServices.findIndex(dc => dc.id === additionalService.id);
    console.log('index ADDING', index);
    console.log('additionalService ADDING', additionalService);
    if (index > -1) {
      currentServices[index] = additionalService;
    } else {
      currentServices.unshift(additionalService);
    }
    this.organizationAdditionalServices.set([...currentServices]);
  }

  getOrganizationAdditionalServices() {
    this.incrementRequests();
    this.BackOfficeService
      .getOrganizationAdditionalServices(this.organization.organizationId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(additionalServices => {
        this.organizationAdditionalServices.set(additionalServices);
        // console.log('organizationAdditionalServices', this.organizationAdditionalServices());
        setTimeout(() => {
          this.dataSourceDiscountCodes.sort = this.sortCheckIn;
        });
        this.decrementRequests();
      },
        () => {
          this.decrementRequests();
        });
  }

  getOrganizationParkingPrices() {
    this.incrementRequests();
    this.BackOfficeService
      .getOrganizationParkingPrices(this.organization.organizationId, this.parkingPricingId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(parkingPrices => {
        this.allParkingPrices = parkingPrices;
        this.organizationParkingPrices.set(parkingPrices);
        if (this.productTypeIds.length > 0) {
          this.changeProductType(this.productTypeIds);
        }
        setTimeout(() => {
          this.dataSourceDiscountCodes.sort = this.sortCheckIn;
        });
        this.decrementRequests();
      },
        () => {
          this.decrementRequests();
        });
  }

  updateParkingPricing(parkingPricing) {
    const currentPricings = this.organizationParkingPrices();
    const index = currentPricings.findIndex(dc => dc.id === parkingPricing.id);
    console.log('index ADDING', index);
    console.log('parkingPricing ADDING', parkingPricing);
    if (index > -1) {
      currentPricings[index] = parkingPricing;
    } else {
      currentPricings.unshift(parkingPricing);
    }
    this.organizationParkingPrices.set([...currentPricings]);
  }

  openEditParkingPricingModal(parkingPricing?: any) {
    if (!this.isLoading) {
      var model: any = parkingPricing ? parkingPricing : null;

      const dialogRef = this.dialog.open(ParkingPricingFormComponent, {
        // height: '1010px',
        // minHeight: '80vh',
        width: '720px',
        hasBackdrop: true,
        data: {
          parkingPricing: model,
          organization: this.organization,
          edit: parkingPricing ? true : false,
        }
      });

      dialogRef.afterOpened().subscribe(() => {
        const componentInstance = dialogRef.componentInstance as ParkingPricingFormComponent;
        componentInstance.saveChangesEvent.subscribe((parkingPricing) => {
          this.updateParkingPricing(parkingPricing);
        });
      });
    }
  }

  openBulkUpdateParkingPricingModal() {
    if (!this.isLoading) {
      // var model: any = parkingPricing ? parkingPricing : null;

      const dialogRef = this.dialog.open(ParkingPricingBulkUpdateFormComponent, {
        // height: '1010px',
        // minHeight: '80vh',
        width: '720px',
        hasBackdrop: true,
        data: {
          parkingId: this.parkingPricingId,
          parkingPricing: this.organizationParkingPrices(),
          organization: this.organization,
          // edit: parkingPricing ? true : false,
        }
      });

      dialogRef.afterOpened().subscribe(() => {
        const componentInstance = dialogRef.componentInstance as ParkingPricingBulkUpdateFormComponent;
        componentInstance.saveChangesEvent.subscribe(() => {
          this.getOrganizationParkingPrices();
        });
      });
    }
  }

  goToOrder(order: Order): void {
    this.order = order;
    const url = this.router.serializeUrl(this.router.createUrlTree([`/admin/back-office24-order/${order.orderId}`]));
    window.open(url, '_blank');
  }

  applyFilter() {
    this.dataSourceDiscountCodes.data = this.organizationDiscountChecks.filter(discountCheck => {
      if (this.selectedFilter === 'expired') {
        return discountCheck.status === "Expired";
      }
      else if (this.selectedFilter === 'active') {
        return discountCheck.status === "Active";
      }

      return true;
    });
  }

  getStatusColor(status) {
    switch (status) {
      case "Active":
        return '#28a745';
      case "Expired":
        return "#dc3545";
      default:
        return '#ffc107';
    }
  }

  changeParking(value: string) {
    this.parkingPricingId = value;
    this.getOrganizationParkingPrices();
  }

  changeProductType(value: number[]) {
    if (value.length > 0) {
      this.productTypeIds = value;
      const currentParkingPrices = this.allParkingPrices.filter(parkingPrice => this.productTypeIds.includes(parkingPrice.productType));
      this.organizationParkingPrices.set([...currentParkingPrices]);
    }
    else {
      this.productTypeIds = [];
      this.organizationParkingPrices.set([...this.allParkingPrices]);
    }
  }

  ngOnDestroy(): void {
    this.orgSubscription.unsubscribe();
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
}
